import { ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EMPTY, Observable, of, Subscription, timer } from 'rxjs';
import { catchError, filter, finalize, first, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { select } from '@angular-redux/store';
import { ModalController, Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import Env, { TermsCheckboxVisibility, WalletV2Step } from '@ridango/core/env';
import {
    AccountTicketsNgrxFacade,
    ApplicationFacade,
    CustomerAccountsFacade,
    NotificationFacade,
    PaymentMethodsFacade,
    PaymentMethodsService,
    PurchaseService,
    ShoppingCartService,
    TicketsService
} from '@ridango/components';
import {
    AppStartupService,
    AuthService,
    BaseComponent,
    InitiatePaymentRequest,
    InitiatePaymentResponse,
    PaymentMethod,
    PaymentPortalService,
    TranslateObjectFieldPipe
} from '@ridango/core';
import { ticketUnitDisplayCount } from '../purchase-page-v2/services/purchase-page-v2-booklet.util';
import { SelectPurposeModalComponent } from './select-purpose-modal/select-purpose-modal.component';
import { OtherPurposeModalComponent } from './other-purpose-modal/other-purpose-modal.component';
import { TermsDialogMobileComponent } from '@ridango/components/lib/core-ui/advanced/terms-dialog-mobile/terms-dialog-mobile.component';
import { AddCompanyModalComponent } from '../payment-settings-page/add-company-modal/add-company-modal.component';
import { CorporateEmployeesService } from '@ridango/components/lib/corporate-employees/corporate-employees.service';
import {
    AreaCode,
    Cart,
    CartPaymentCard,
    CartPaymentMethod,
    CartPaymentMethodsDetails,
    CartStatus,
    Product,
    ProductArea,
    SameTicketsGroup,
    Ticket
} from '@ridango/components/lib/purchase/purchase.model';
import {
    CustomerCreditInfo,
    Organization,
    TripPurposesTypes,
    PayerType,
    TripPurpose
} from '@ridango/components/lib/corporate-employees/corporate-employees.models';
import { V2NavigateAwayModalComponent } from './navigate-away-modal/navigate-away-modal.component';
import { DeleteSavedPaymentMethodModalComponent } from './payment-methods/delete-saved-payment-method-modal/delete-saved-payment-method-modal.component';
import { DeviceIdService } from '@ridango/core/lib/services';
import { ValueCodeModalComponent } from './value-code-modal/value-code-modal.component';
import { DeleteValueCodeModalComponent } from './delete-value-code-modal/delete-value-code-modal.component';
import { ShoppingCartIconService } from '@ridango/components/lib/shopping-cart/shopping-cart-icon.service';
import { EmptyCartOptions } from '@ridango/core/lib/enums/features.enum';
import { SelectNewPaymentMethodModalComponent } from './payment-methods/select-new-payment-method-modal/select-new-payment-method-modal.component';
import { LeavePurchaseModalComponent } from './leave-purchase-modal/leave-purchase-modal.component';
import { AnalyticsService } from '@ridango/core/lib/services/analytics.service';
import { TrackedEventCategory } from '@ridango/core/lib/enums/analytics/tracked-event-categories.enum';
import { TrackedEventAction } from '@ridango/core/lib/enums/analytics/tracked-event-action.enum';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
    templateUrl: './shopping-cart-page.component.html',
    styleUrls: ['./shopping-cart-page.component.scss']
})
export class ShoppingCartPageV2Component extends BaseComponent implements OnInit, OnDestroy {
    public steps: (string | WalletV2Step)[] = this.env.PURCHASE_FLOW.TICKET_STEPS;
    public stepsDescription = this.env.PURCHASE_FLOW.TICKET_STEPS_DESCRIPTION ?? [];
    public tickets = [];
    public cart: Cart;
    public methods = [];
    public lang = this.translate.currentLang;
    public termsUrl: string;
    public selectedMethod: CartPaymentMethod | CartPaymentCard = null;
    public assetsPath$ = this.applicationFacade.assetsPath$;
    public payerType = PayerType;
    public selectedPayerType = PayerType.personal;
    public organizations: Organization[] = [];
    public selectedOrganization: Organization;
    public availableCountryCallingCodes: string[] = [];
    public selectedPurpose: TripPurpose = null;
    public otherPurposeValue = '';
    public isCheckPurposeValue = false;
    public firstInitCart = false;
    public termsAgreed = false;
    public removeMode = false;
    public physicalBackButtonSubscription$: Subscription;
    public shoppingCartTimer$: Subscription;
    showNavigationModal = false;
    public cartLoading = false;
    public isShowTermsAndConditions = false;
    public showOtherPaymentPortalMethod = false;
    public paymentPortalMethodDetails: CartPaymentMethodsDetails = null;
    public paymentPortalProviderMethod: PaymentMethod;
    emptyCartOptions = EmptyCartOptions;
    public paymentIsCreating = false;
    public productAreas: ProductArea[] = [];
    public activationUrl: string;
    currency = this.appStartupService.getCurrency();
    sameTicketsGroup: SameTicketsGroup[];
    paymentFlow: string;
    modalOpened = false;
    public currentLanguage = this.translate.currentLang;
    public accountsWithDisplayData$ = this.customerAccountsFacade.accountsWithDisplayData$;
    public preSelectedCompany = false;
    public customerAccountId: number | string;
    public selectedAccountWithDisplayData$ = this.customerAccountsFacade.selectedAccountWithDisplayData$;
    public tripPurposes = TripPurposesTypes;
    public validOtherPurposeLength = this.shoppingCartService.getValidOtherPurposeLength;
    public purposeValueValidated = true;
    public isPurposeInputTouched = false;

    public isAddButtonDisabled$ = this.purchaseService.ticketCount$.pipe(
        map((count) => count >= this.purchaseService.MAX_TICKETS_AMOUNT)
    );

    protected readonly PayerType = PayerType;

    private companyPurposes: TripPurpose[] = [];
    private readonly YOUR_TICKETS_IN_CART = 'Your tickets in cart';
    private readonly REMOVE = 'Remove';
    private readonly ADD_ANOTHER_TICKET = 'Add another ticket';
    private readonly PAYMENT_METHOD = 'Payment method';
    private readonly PROCEED_TO_PAYMENT = 'Proceed to payment';

    @select(['application', 'highContrast']) highContrast$: Observable<boolean>;
    @ViewChild('cartContent') cartContent?: ElementRef;

    private readonly PAYER_TYPE = 'Payer type';

    constructor(
        @Inject('ENV') public env: Env,
        private router: Router,
        private translate: TranslateService,
        private purchaseService: PurchaseService,
        private paymentMethodService: PaymentMethodsService,
        private route: ActivatedRoute,
        private applicationFacade: ApplicationFacade,
        private modalCtrl: ModalController,
        private matDialog: MatDialog,
        private paymentFacade: PaymentMethodsFacade,
        private corporateEmployeesService: CorporateEmployeesService,
        private shoppingCartService: ShoppingCartService,
        private shoppingCartIconService: ShoppingCartIconService,
        private accountTicketsFacade: AccountTicketsNgrxFacade,
        private paymentPortalService: PaymentPortalService,
        private notifications: NotificationFacade,
        private cdr: ChangeDetectorRef,
        private platform: Platform,
        private translatePipe: TranslateObjectFieldPipe,
        private authService: AuthService,
        private deviceIdService: DeviceIdService,
        public appStartupService: AppStartupService,
        private analyticsService: AnalyticsService,
        public ticketsService: TicketsService,
        private customerAccountsFacade: CustomerAccountsFacade
    ) {
        super();
    }

    ngOnInit() {
        if (this.env.IS_NEW_AFC) {
            this.customerAccountsFacade.selectedAccount$
                .pipe(
                    filter((account) => !!account && account.type?.code === 'CORPORATE'),
                    switchMap((account) =>
                        this.getCustomerCreditBalance(account.id).pipe(map((balance) => ({ account, balance })))
                    ),
                    switchMap(({ account, balance }) =>
                        this.accountsWithDisplayData$.pipe(
                            map((accounts) => accounts.find((acc) => acc.id === account.id.toString())),
                            map((accountWithDisplay) => ({ account, balance, accountWithDisplay }))
                        )
                    ),
                    takeUntil(this.componentDestroyed$)
                )
                .subscribe(({ account, balance, accountWithDisplay }) => {
                    if (!accountWithDisplay?.organization) {
                        return;
                    }

                    this.customerAccountId = account.id;
                    this.selectedPayerType = PayerType.company;
                    this.preSelectedCompany = true;

                    const organization: Organization = {
                        company_name: accountWithDisplay.organization.name,
                        employee_id: Number(account.id),
                        credit: {
                            balance,
                            active: accountWithDisplay.isActive,
                            valid_until: accountWithDisplay.account.endDate
                                ? new Date(accountWithDisplay.account.endDate)
                                : null
                        }
                    };
                    this.organizations = [organization];
                });
        } else if (this.env.IS_CORPORATE && this.authService.getLoginStatus()) {
            this.corporateEmployeesService
                .getEmployeeOrganizations()
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe((organizations) => {
                    this.organizations = organizations;
                    this.setDefaultCompanyMethod();
                });
        }

        //TODO: Fix Purchase-v2 cart locking users in live after they click back from payment portal,  commented out because was breaking VL
        /*     this.router.events
            .pipe(
                takeUntil(this.componentDestroyed$),
                filter((event) => event instanceof NavigationStart),
                filter(() => this.isCartLocked() && !this.paymentIsCreating), //
                switchMap(() => this.showNavigateAwayModal())
            )
            .subscribe(); */

        this.route.data.pipe(takeUntil(this.componentDestroyed$)).subscribe((data) => {
            this.paymentFlow = data['paymentFlow'];
            if (this.paymentFlow === 'top-up') {
                this.steps = this.env.WALLET_V2?.STEPS ?? [];
            } else {
                this.steps = this.env.PURCHASE_FLOW.TICKET_STEPS;
            }
        });

        this.isShowTermsAndConditions = this.resolveTermsCheckboxVisibility();

        if (this.env.PURCHASE_FLOW?.FREE_TRAVEL_MESSAGE_IN_DESCRIPTION) {
            this.purchaseService
                .getAreas()
                .pipe(
                    switchMap((areas) =>
                        this.purchaseService.getProductAreas(areas.find((a) => a.code === AreaCode.area)?.id)
                    )
                )
                .subscribe((areas) => {
                    this.productAreas = areas;
                    this.refreshGroupedCartTickets();
                });
        }

        if (this.env.PURCHASE_FLOW?.TICKET_CONF?.SHOPPING_CART?.ZONES_IN_TICKET_NAME) {
            this.purchaseService
                .getProductAreas()
                .pipe(take(1))
                .subscribe((areas) => {
                    this.productAreas = areas;
                    this.refreshGroupedCartTickets();
                });
        }

        this.purchaseService.activationUrl$.pipe(takeUntil(this.componentDestroyed$)).subscribe((activationUrl) => {
            this.activationUrl = activationUrl;
        });

        this.physicalBackButtonSubscription$ = this.platform.backButton.subscribeWithPriority(1000, () => {
            if (this.env.PURCHASE_FLOW?.HIDE_SHOPPING_CART_BACK_BUTTON) {
                this.handleClosePurchasePage();
                return;
            }
            this.handleBackClicked();
        });

        const currentCartId = this.shoppingCartService.getCartId();

        if (currentCartId && !this.cartLoading) {
            this.getCart();
        }

        this.translate.onLangChange.pipe(takeUntil(this.componentDestroyed$)).subscribe((event) => {
            this.lang = event.lang;
        });

        this.applicationFacade.termsUrl$.pipe(takeUntil(this.componentDestroyed$)).subscribe((value) => {
            this.termsUrl = value;
        });

        this.corporateEmployeesService
            .getCountryCallingCodes()
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((codes) => {
                this.availableCountryCallingCodes = codes;
            });

        if (!this.env.IS_NEW_AFC && this.env.IS_CORPORATE && this.authService.getLoginStatus()) {
            this.corporateEmployeesService
                .getEmployeeOrganizations()
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe((organizations) => {
                    this.organizations = organizations;
                    this.setDefaultCompanyMethod();
                });
        }

        // new afc requires cart id, and payments methods should be request on cart id changed
        if (!this.env.IS_NEW_AFC) {
            this.getPaymentMethods();
        }

        this.paymentFacade
            .getCartRedirectUrl()
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((url) => {
                if (url) {
                    if (url.includes('cart')) {
                        this.redirectFromCart(url.split('/'));
                    }
                    this.paymentFacade.setCartRedirectUrl(null);
                }
            });

        this.fetchMethods();

        this.shoppingCartTimer$ = this.shoppingCartIconService.timer$.subscribe((timer) => {
            if (timer === '00:00') {
                this.cart = null;
            }
        });

        //Try to fetch trip purposes when a corporate customer has been selected
        if (this.env.IS_NEW_AFC) {
            this.selectedAccountWithDisplayData$
                .pipe(
                    first((acc) => acc?.isCorporate),
                    switchMap(() => this.corporateEmployeesService.getTripPurposes()),
                    tap((purposes) => {
                        this.companyPurposes = purposes;
                    }),
                    catchError((error) => {
                        console.error(error);
                        return of(null);
                    })
                )
                .subscribe();
        }
    }

    private recreateShoppingCart(cartId: number): void {
        this.shoppingCartService
            .createCartCopy(cartId)
            .pipe(take(1), takeUntil(this.componentDestroyed$))
            .subscribe(() => {
                this.getCart();
            });
    }

    private handleLockedCart(cartId: number): void {
        this.shoppingCartService
            .getShoppingCartStatus(cartId)
            .pipe(
                take(1),
                filter(
                    (cart) =>
                        cart.status === CartStatus.LOCKED ||
                        cart.status === CartStatus.FAILED ||
                        cart.status === CartStatus.ABANDONED ||
                        cart.status === CartStatus.CANCELLED
                ),
                takeUntil(this.componentDestroyed$)
            )
            .subscribe(() => {
                this.recreateShoppingCart(cartId);
            });
    }

    async showNavigateAwayModal() {
        if (this.modalOpened) {
            return false; //Flag for not retriggering modal after dismissing it because of NavigationStart start gets called again
        }

        const modal = await this.modalCtrl.create({
            component: V2NavigateAwayModalComponent
        });

        await modal.present();

        modal.onDidDismiss().then(({ data }) => {
            if (data) {
                this.modalOpened = true;
                this.shoppingCartService.setCartId(null);
                this.paymentFacade.setCartStatus(null);
                this.shoppingCartIconService.initializeHeaderShoppingCart(0, null);
                this.router.navigate([this.translate.currentLang, 'account', 'tickets']);
            }
        });
    }

    setDefaultCompanyMethod(): void {
        const defaultCompanyMethod = this.organizations.find((organization) => organization.default_payment);

        if (defaultCompanyMethod) {
            this.selectedMethod = null;
            this.selectedPayerType = PayerType.company;
            this.onOrganizationSelected(defaultCompanyMethod);
        }
    }

    getPaymentMethods() {
        if (!this.env.PURCHASE_FLOW?.ENABLE_DETAILED_PAYMENT_METHODS) {
            return;
        }
        this.shoppingCartService
            .getPaymentMethods(this.cart?.shoppingCartId)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(
                (response: CartPaymentMethodsDetails) => {
                    this.processPaymentMethods(response);
                },
                (error) => {
                    console.error(error);
                    this.notifications.error(this.translate.instant('PAYMENT_METHODS_LOADING_ERROR'));
                }
            );
    }

    processPaymentMethods(methodsResponse: CartPaymentMethodsDetails) {
        this.paymentPortalMethodDetails = methodsResponse;
        this.showOtherPaymentPortalMethod =
            !methodsResponse.cards?.length || this.env.PURCHASE_FLOW?.SHOW_OTHER_PAYMENT_METHODS;

        if (methodsResponse.defaultPaymentCardId) {
            this.selectedMethod = methodsResponse.cards.find((c) => c.id === methodsResponse.defaultPaymentCardId);
        } else if (methodsResponse.defaultPosPaymentMethodId) {
            this.selectedMethod = methodsResponse.methods.find(
                (m) => m.posPaymentMethodId === methodsResponse.defaultPosPaymentMethodId
            );
            this.showOtherPaymentPortalMethod = true;
        }
    }

    getCartTickets(cart: Cart) {
        return this.shoppingCartService.getShoppingCartStatus(cart.shoppingCartId).pipe(
            take(1),
            map((status) => ({ cart, tickets: status?.tickets }))
        );
    }

    async handleClosePurchasePage(): Promise<void> {
        const modal = await this.modalCtrl.create({
            component: LeavePurchaseModalComponent,
            cssClass: 'leave-purchase-modal',
            backdropDismiss: false
        });

        await modal.present();

        const { role } = await modal.onDidDismiss();
        if (role === 'exit') {
            this.removeShoppingCart();
        }
    }

    removeShoppingCart() {
        this.purchaseService.resetProductsLocalStorage();
        this.paymentFacade.setCartStatus(null);
        this.shoppingCartService.setCartId(null);
        this.shoppingCartIconService.initializeHeaderShoppingCart(0, null);
        this.router.navigate([this.translate.currentLang, 'account', 'tickets']);
    }

    getCart() {
        const currentCartId = this.shoppingCartService.getCartId();
        this.cartLoading = true;
        this.shoppingCartService
            .getShoppingCartStatus(currentCartId)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(
                async (currentCart) => {
                    this.cartLoading = false;
                    if (
                        currentCart.status === CartStatus.ON_HOLD ||
                        currentCart.status === CartStatus.LOCKED ||
                        currentCart.status === CartStatus.OPEN ||
                        currentCart.status === CartStatus.FAILED ||
                        currentCart.status === CartStatus.ABANDONED ||
                        currentCart.status === CartStatus.CANCELLED
                    ) {
                        this.cart = currentCart;

                        // Distribute value codes to tickets if configured
                        if (this.env.PURCHASE_FLOW?.TICKET_CONF?.SHOPPING_CART?.SHOW_VALUE_CODES_PER_TICKET) {
                            this.cart = this.distributeValueCodesToTickets(this.cart);
                        }

                        if (this.env.IS_NEW_AFC) {
                            this.shoppingCartService
                                .getPaymentMethods(this.cart.shoppingCartId)
                                .pipe(takeUntil(this.componentDestroyed$))
                                .subscribe((response: CartPaymentMethodsDetails) => {
                                    this.processPaymentMethods(response);
                                });
                        }

                        this.enrichCartTicketsWithLocalProducts(this.cart.tickets);

                        this.sameTicketsGroup = this.cart?.tickets?.length
                            ? this.groupSameTickets(this.cart.tickets)
                            : [];

                        this.restoreLocalProductsFromCart(this.cart.tickets);

                        const cartUnitCount = this.cartTicketUnitCount(this.cart.tickets);
                        this.purchaseService.updateCartTicketCount(cartUnitCount);
                        this.firstInitCart = true;
                        this.paymentFacade.setCartStatus(this.cart.status);
                        this.shoppingCartIconService.initializeHeaderShoppingCart(
                            cartUnitCount,
                            this.cart.deletionTimestamp
                        );
                        this.cdr.detectChanges();
                    } else {
                        this.shoppingCartService.setCartId(null);
                        this.paymentFacade.setCartStatus(null);
                    }
                },
                (error) => {
                    this.cartLoading = false;

                    if (error.status === 409) {
                        this.shoppingCartService.setCartId(null);
                        this.paymentFacade.setCartStatus(null);
                    } else {
                        this.notifications.error(this.translate.instant('CART_LOADING_ERROR'));
                    }
                }
            );
    }

    getProductDescription(productDescription, zoneIds: { startZoneId: number; endZoneId: number }) {
        return this.purchaseService.getProductDescription(this.productAreas, {
            description: productDescription,
            startZoneId: zoneIds.startZoneId,
            endZoneId: zoneIds.endZoneId
        });
    }

    getZonesString(ticket: Ticket): string {
        const startZone = this.productAreas?.find((area) => area.startZoneId === ticket.startZoneId);
        const endZone = this.productAreas?.find((area) => area.endZoneId === ticket.endZoneId);

        if (ticket.startZoneId === ticket.endZoneId && startZone) {
            const zoneName = this.translatePipe.transform(startZone.name, null, this.currentLanguage);
            return `${zoneName}`;
        }

        if (startZone && endZone) {
            const startZoneName = this.translatePipe.transform(startZone.name, null, this.currentLanguage);
            const endZoneName = this.translatePipe.transform(endZone.name, null, this.currentLanguage);
            return `${startZoneName}, ${endZoneName}`;
        }

        return '';
    }

    private ticketDisplayCount(ticket: Ticket): number {
        return ticketUnitDisplayCount(ticket);
    }

    private cartTicketUnitCount(tickets: Ticket[]): number {
        return tickets.reduce((sum, ticket) => sum + ticketUnitDisplayCount(ticket), 0);
    }

    private restoreLocalProductsFromCart(tickets: Ticket[]): void {
        if (!tickets?.length) {
            return;
        }

        const products = tickets.map((ticket) => ({
            id: ticket.productId,
            productId: ticket.productId,
            amount: ticketUnitDisplayCount(ticket),
            startZoneId: ticket.startZoneId,
            endZoneId: ticket.endZoneId,
            email: ticket.email,
            cardId: ticket.cardId,
            starting: ticket.starting,
            dynamicProductParameters: ticket.dynamicProductParameters || {}
        })) as unknown as Product[];

        this.purchaseService.setArrayLocalProducts(products);
    }

    private enrichCartTicketsWithLocalProducts(tickets: Ticket[]): void {
        if (!tickets?.length) {
            return;
        }

        const localProducts = this.purchaseService.getLocalProducts() as Array<
            Product & {
                cardId?: string;
                email?: string;
                product_properties?: unknown[];
                productProperties?: unknown[];
            }
        >;

        if (!localProducts?.length) {
            return;
        }

        tickets.forEach((ticket) => {
            const matchingProduct = localProducts.find((product) => {
                return (
                    String(product.productId ?? product.id) === String(ticket.productId) &&
                    product.startZoneId === ticket.startZoneId &&
                    product.endZoneId === ticket.endZoneId &&
                    (product.cardId || null) === (ticket.cardId || null)
                );
            });

            if (!matchingProduct) {
                return;
            }

            const productProperties = matchingProduct.product_properties || matchingProduct.productProperties;
            if (!productProperties?.length) {
                return;
            }

            (ticket as Ticket & { product?: unknown }).product = {
                ...(ticket as Ticket & { product?: Record<string, unknown> }).product,
                ...matchingProduct,
                product_properties: productProperties
            };
        });
    }

    private refreshGroupedCartTickets(): void {
        if (!this.cart?.tickets?.length) {
            return;
        }

        this.sameTicketsGroup = this.groupSameTickets(this.cart.tickets);
        this.cdr.detectChanges();
    }

    private groupSameTickets(tickets: Ticket[]): { ticket: Ticket; count: number; totalPrice: number }[] {
        const ticketMap = new Map<string, { ticket: Ticket; count: number; totalPrice: number }>();
        let singleGroupTicketKeyIncrement = 0;
        tickets.forEach((ticket) => {
            const isSingleTicketGroup =
                ticket?.dynamicProductParameters && Object.keys(ticket.dynamicProductParameters).length > 0;

            let key = `${ticket.productId}-${ticket.endZoneId}-${ticket.startZoneId}`;
            if (ticket.cardId) {
                key += `-${ticket.cardId}`;
            }
            if (ticket.parametersCode) {
                key += `-${ticket.parametersCode}`;
            }
            if (isSingleTicketGroup) {
                singleGroupTicketKeyIncrement++;
                key += `-g-${singleGroupTicketKeyIncrement}`;
            }
            const lineCount = this.ticketDisplayCount(ticket);

            if (ticketMap.has(key)) {
                const ticketGroup = ticketMap.get(key);
                ticketGroup.count += lineCount;
                ticketGroup.totalPrice += ticket?.price?.amount;
            } else {
                ticket.description = this.getProductDescription(ticket.description, {
                    startZoneId: ticket.startZoneId,
                    endZoneId: ticket.endZoneId
                });
                const zoneString = this.getZonesString(ticket);
                ticket.zonesString = zoneString ? `${this.translate.instant('ZONES')}: ${zoneString}` : '';

                ticket.ticketTypes = isSingleTicketGroup
                    ? this.ticketsService.getSelectedTravellerTypesOnTicket(
                          ticket.userProfiles[0].companionProfiles,
                          ticket.dynamicProductParameters
                      )
                    : '';
                ticket.isSingleTicketGroup = isSingleTicketGroup;

                // Only add to map if we have valid ticket data
                if (ticket && ticket.productId) {
                    ticketMap.set(key, {
                        ticket,
                        count: lineCount,
                        totalPrice: ticket?.price?.amount
                    });
                } else {
                    console.warn('Invalid ticket data when grouping:', ticket);
                }
            }
        });

        return Array.from(ticketMap.values());
    }

    /**
     * Distributes cart-level value codes to individual tickets based on their discount amounts
     */
    private distributeValueCodesToTickets(cart: Cart): Cart {
        if (!cart.valueCodes || cart.valueCodes.length === 0 || !cart.tickets) {
            return cart;
        }

        // Clone cart to avoid mutations
        const updatedCart = { ...cart, tickets: [...cart.tickets] };

        // Find tickets with discounts
        const ticketsWithDiscounts = updatedCart.tickets.filter(
            (ticket) => ticket.price?.discountAmount && ticket.price.discountAmount > 0
        );

        if (ticketsWithDiscounts.length === 0) {
            return updatedCart;
        }

        // Assign value codes to tickets with discounts
        // If there's only one value code, assign it to the first ticket with discount
        // If multiple, try to match by discount amount or distribute them
        if (updatedCart.valueCodes.length === 1 && ticketsWithDiscounts.length === 1) {
            // Simple case: one code, one discounted ticket
            const ticketIndex = updatedCart.tickets.indexOf(ticketsWithDiscounts[0]);
            updatedCart.tickets[ticketIndex] = {
                ...updatedCart.tickets[ticketIndex],
                valueCodes: [updatedCart.valueCodes[0]]
            };
        } else {
            // Multiple codes or multiple tickets - assign to all discounted tickets
            ticketsWithDiscounts.forEach((ticket) => {
                const ticketIndex = updatedCart.tickets.indexOf(ticket);
                updatedCart.tickets[ticketIndex] = {
                    ...updatedCart.tickets[ticketIndex],
                    valueCodes: updatedCart.valueCodes
                };
            });
        }

        // Clear cart-level value codes when distributed to tickets
        updatedCart.valueCodes = [];

        return updatedCart;
    }

    getPriceString(price: number) {
        const formattedCents = price % 100 < 10 ? '0' + (price % 100) : '' + (price % 100);

        return `${Math.floor(price / 100)},${formattedCents}`;
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.physicalBackButtonSubscription$.unsubscribe();
        this.paymentFacade.setCartStatus(null);
        this.shoppingCartTimer$.unsubscribe();
    }

    async handleBackClicked(): Promise<void> {
        if (this.isCartLocked()) {
            await this.redirectFromCart([this.translate.currentLang, 'purchase-v2']);
        } else if (this.activationUrl) {
            const isRecentPurchaseFlow = this.activationUrl.includes('/recent');

            if (isRecentPurchaseFlow) {
                this.purchaseService.resetProductsLocalStorage();
                this.shoppingCartService.setCartId(null);
                this.paymentFacade.setCartStatus(null);
                this.shoppingCartIconService.initializeHeaderShoppingCart(0, null);
                const currentUrlParts = this.activationUrl.split('?')[0].split('/');
                const purchaseFlowStepOneUrl = currentUrlParts.slice(0, 4).join('/');
                await this.router.navigateByUrl(purchaseFlowStepOneUrl);
            } else {
                await this.router.navigate([this.activationUrl]);
            }
        } else {
            window.history.back();
        }
    }

    async redirectFromCart(url: Array<string>) {
        const modal = await this.modalCtrl.create({
            component: V2NavigateAwayModalComponent,
            cssClass: 'navigate-away-modal'
        });

        await modal.present();

        modal.onDidDismiss().then(({ data }) => {
            if (data) {
                this.shoppingCartService.setCartId(null);
                this.paymentFacade.setCartStatus(null);
                this.shoppingCartIconService.initializeHeaderShoppingCart(0, null);

                timer(100)
                    .pipe(takeUntil(this.componentDestroyed$))
                    .subscribe(() => {
                        this.router.navigate(url);
                    });
            }
        });
    }

    handleAddTicketClicked() {
        this.analyticsService.eventTrack(TrackedEventAction.CLICKED, {
            category: TrackedEventCategory.PURCHASE_FLOW,
            name: `${this.YOUR_TICKETS_IN_CART} - ${this.ADD_ANOTHER_TICKET}`
        });

        if (this.isCartLocked()) {
            this.notifications.error(this.translate.instant('CART_LOCKED_ERROR'));
            return;
        }

        if (this.paymentFlow === 'top-up') {
            this.router.navigate([this.translate.currentLang, 'purchase', 'wallet']);
        } else {
            this.router.navigate([this.translate.currentLang, 'purchase-v2'], {
                queryParams: { isAddOneMoreTicket: true }
            });
        }
    }

    backToPurchaseFlow() {
        this.router.navigate([this.translate.currentLang, 'purchase-v2']);
    }

    backToTicket() {
        this.router.navigate([this.translate.currentLang, 'account', 'tickets']);
    }

    methodSelected(pm: CartPaymentMethod | CartPaymentCard) {
        if (pm && 'displayName' in pm) {
            this.analyticsService.eventTrack(TrackedEventAction.SELECTED, {
                category: TrackedEventCategory.PURCHASE_FLOW,
                name: `${this.PAYMENT_METHOD} - ${pm.displayName}`
            });
        }
        this.selectedMethod = pm;
        this.cdr.detectChanges();
    }

    async handleProceedToPaymentClicked() {
        this.analyticsService.eventTrack(TrackedEventAction.CLICKED, {
            category: TrackedEventCategory.PURCHASE_FLOW,
            name: this.PROCEED_TO_PAYMENT
        });
        if (this.paymentIsCreating) {
            return;
        }

        this.purchaseService.resetProductsLocalStorage();
        this.isPurposeInputTouched = true;
        if (this.isZeroSumCart) {
            return this.handleZeroPricePayment();
        }

        if (this.selectedPayerType === PayerType.personal) {
            if ((this.selectedMethod as CartPaymentMethod)?.merchantType === 'NETAXEPT') {
                const modal = await this.modalCtrl.create({
                    component: SelectNewPaymentMethodModalComponent,
                    cssClass: 'select-new-payment-method-modal',
                    componentProps: {}
                });
                await modal.present();

                modal.onDidDismiss().then(async ({ data }) => {
                    if (typeof data === 'boolean') {
                        this.handlePersonalPayment(data);
                    }
                });
            } else {
                this.handlePersonalPayment();
            }
        } else {
            this.handleCorporatePayment();
        }
    }

    public toggleTermsAgreed() {
        this.termsAgreed = !this.termsAgreed;
    }

    public onOrganizationSelected(company: Organization): void {
        this.selectedOrganization = company;
        if (!company) {
            return;
        }

        if (!this.env.IS_NEW_AFC) {
            this.corporateEmployeesService
                .getOrganizationPurposes(company?.employee_id)
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe((purposes: TripPurpose[]) => {
                    this.companyPurposes = purposes;
                    this.companyPurposes.push({
                        title: TripPurposesTypes.other,
                        org_id: company.employee_id,
                        id: 0
                    });
                });
        }
    }

    public async handleSelectPurposeClick(): Promise<void> {
        const modal = await this.modalCtrl.create({
            component: SelectPurposeModalComponent,
            cssClass: 'select-purpose-modal',
            componentProps: {
                purposes: this.companyPurposes,
                selectedPurpose: this.selectedPurpose
            }
        });

        await modal.present();

        modal.onDidDismiss().then(async ({ data, role }) => {
            if (role === 'submit') {
                this.selectedPurpose = data;
                this.isPurposeInputTouched = false;

                if (this.selectedPurpose?.title === TripPurposesTypes.other) {
                    await this.handleOtherPurpose();
                }
            }
        });
    }

    public async handleShowTermsConditions(): Promise<void> {
        const termsDialog = this.matDialog.open(TermsDialogMobileComponent, {
            data: { termsUrl: this.termsUrl, withAgreeButton: true },
            autoFocus: true,
            width: '90%',
            maxWidth: '80vh',
            panelClass: 'terms-dialog-mobile-wrapper'
        });

        termsDialog
            .afterClosed()
            .pipe(take(1))
            .subscribe((termsAgreed) => (this.termsAgreed = termsAgreed));
    }

    public onChangePayerType(): void {
        this.analyticsService.eventTrack(TrackedEventAction.CLICKED, {
            category: TrackedEventCategory.PURCHASE_FLOW,
            name: `${this.PAYMENT_METHOD} - ${this.PAYER_TYPE}: ${this.selectedPayerType}`
        });

        this.methodSelected(null);
        this.termsAgreed = false;
    }

    public async handleAddCompanyClicked() {
        if (!this.availableCountryCallingCodes?.length) {
            return;
        }

        const modal = await this.modalCtrl.create({
            component: AddCompanyModalComponent,
            cssClass: 'add-company-modal',
            componentProps: {
                availableCountryCallingCodes: this.availableCountryCallingCodes
            }
        });

        await modal.present();

        modal.onDidDismiss().then(async (data) => {
            if (data.data && this.env.IS_CORPORATE && this.authService.getLoginStatus()) {
                this.corporateEmployeesService
                    .getEmployeeOrganizations()
                    .pipe(takeUntil(this.componentDestroyed$))
                    .subscribe((organizations) => {
                        this.organizations = organizations;
                    });
            }
        });
    }

    public handleDeleteTicket(ticketId: number | string) {
        this.shoppingCartService
            .deleteTicket(this.cart.shoppingCartId, ticketId)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(() => {
                const ticket = this.env.IS_NEW_AFC
                    ? this.cart.tickets.find((t) => t.productId == ticketId)
                    : this.cart.tickets.find((t) => t.id == ticketId);

                const productId = ticket?.productId || ticket?.id;

                if (productId) {
                    this.purchaseService.reduceLocalTicketTypeAmount({ id: productId } as Product);
                }

                if (!(this.cart.tickets.length - 1)) {
                    this.purchaseService.resetProductsLocalStorage();
                    this.shoppingCartService.setCartId(null);
                    this.shoppingCartIconService.initializeHeaderShoppingCart(0, null);
                    if (this.env.IS_MOBILE_APP) {
                        this.cart.tickets = [];
                    } else {
                        this.router.navigate([this.translate.currentLang, 'purchase-v2']);
                    }
                } else {
                    this.getCart();
                }
            });
    }

    public handleDeleteValueCode(code) {
        this.shoppingCartService
            .deleteValueCode(this.cart.shoppingCartId, code)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(
                () => {
                    this.notifications.success(this.translate.instant('CART_VALUE_CODE_DELETED_SUCCESSFULLY'));
                    this.getCart();
                },
                (e) => {
                    this.notifications.error(
                        this.translate.instant(
                            e.error && !this.env.IS_NEW_AFC ? e.error : 'CART_VALUE_CODE_COMMON_ERROR'
                        )
                    );
                }
            );
    }

    public switchRemoveMode() {
        if (this.isCartLocked()) {
            this.notifications.error(this.translate.instant('CART_LOCKED_ERROR'));
            return;
        }

        this.removeMode = !this.removeMode;

        const eventAction = this.removeMode ? TrackedEventAction.OPENED : TrackedEventAction.CLOSED;
        this.analyticsService.eventTrack(eventAction, {
            category: TrackedEventCategory.PURCHASE_FLOW,
            name: `${this.YOUR_TICKETS_IN_CART} - ${this.REMOVE}`
        });
    }
    public isCorporatePurpose(): boolean {
        const isOtherPurposeInvalid =
            this.selectedPurpose?.title === TripPurposesTypes.other &&
            (!this.otherPurposeValue || this.otherPurposeValue.length < this.validOtherPurposeLength);

        const hasValidPurpose = this.selectedPurpose?.title && !isOtherPurposeInvalid;

        return this.selectedPayerType === PayerType.company && !hasValidPurpose;
    }

    public enoughCorporateBalance(): boolean {
        return (
            this.selectedPayerType === PayerType.personal ||
            this.selectedOrganization?.credit.balance >= this.cart?.price.amount / 100
        );
    }

    public isCorporateCreditActive(): boolean {
        return this.selectedOrganization?.credit.active;
    }

    public async handleRedeemValueCode() {
        const modal = await this.modalCtrl.create({
            component: ValueCodeModalComponent,
            cssClass: 'value-code-modal ',
            componentProps: { cartId: this.cart.shoppingCartId }
        });

        await modal.present();

        const title = modal.querySelector<HTMLElement>('[data-modal-title]');

        //TODO: check if needed after https://int.jira.ridango.com/browse/STW-897
        if (title) {
            title.focus();
        }

        modal.onDidDismiss().then(async (data) => {
            if (data.data) {
                this.getCart();
            }
        });
    }

    public async openDeleteValueCodeModal(code) {
        const modal = await this.modalCtrl.create({
            component: DeleteValueCodeModalComponent,
            cssClass: 'delete-value-code-modal',
            componentProps: {
                code
            }
        });

        await modal.present();

        modal.onDidDismiss().then(async ({ data }) => {
            if (data) {
                this.shoppingCartService
                    .deleteValueCode(this.cart.shoppingCartId, code.serialNumber)
                    .pipe(takeUntil(this.componentDestroyed$))
                    .subscribe(
                        () => {
                            this.notifications.success(this.translate.instant('CART_VALUE_CODE_DELETED_SUCCESSFULLY'));
                            this.getCart();
                        },
                        (e) => {
                            this.notifications.error(
                                this.translate.instant(e.error ? e.error : 'CART_VALUE_CODE_COMMON_ERROR')
                            );
                        }
                    );
            }
        });
    }

    private scrollToCartContentBottom(): void {
        const maxScroll = this.cartContent?.nativeElement.scrollHeight;
        this.cartContent?.nativeElement.scrollTo({ top: maxScroll, behavior: 'smooth' });
    }

    private async handleOtherPurpose(): Promise<void> {
        const modal = await this.modalCtrl.create({
            component: OtherPurposeModalComponent,
            cssClass: 'other-purpose-modal',
            componentProps: {
                purposeValue: this.otherPurposeValue
            }
        });

        await modal.present();

        modal.onDidDismiss().then(async (data) => {
            this.otherPurposeValue = data.data;
            this.isCheckPurposeValue = true;
            this.scrollToCartContentBottom();
        });
    }

    private handlePersonalPayment(saveMethod: boolean = true) {
        const currentCartId = this.shoppingCartService.getCartId();
        const ticketId = this.cart.tickets.length === 1 ? this.cart.tickets[0].id : undefined;
        if (!currentCartId) {
            this.notifications.error(this.translate.instant('LOCAL_NO_CART_FOUND'));
            window.scroll(0, 0);
            return;
        }
        if (this.isCartLocked()) {
            this.handleLockedCartPayment(saveMethod, ticketId);
        } else {
            // status will be updated after returning to this page. update status here to avoid case when payment success and user should be redirected but stack on this page
            this.paymentFacade.setCartStatus(null);
            this.paymentIsCreating = true;

            this.accountTicketsFacade.resetAccountTickets();

            if (!this.selectedMethod && this.env.IS_NEW_AFC) {
                this.notifications.error(this.translate.instant('SHOPPING_CART_CHOOSE_PAYMENT_METHOD'));
                this.paymentIsCreating = false;
                return;
            }

            let merchantType = (this.selectedMethod as CartPaymentMethod)?.merchantType;

            if (!merchantType && this.selectedMethod && 'paymentMethodId' in this.selectedMethod) {
                const card = this.selectedMethod as CartPaymentCard;
                const method = this.paymentPortalMethodDetails?.methods?.find(
                    (m) => m.paymentMethodId === card.paymentMethodId
                );
                merchantType = method?.merchantType;
            }

            if (!merchantType && this.paymentPortalMethodDetails?.methods?.length > 0) {
                merchantType = this.paymentPortalMethodDetails.methods[0].merchantType;
            }

            if (this.env.IS_NEW_AFC && !merchantType) {
                if (!this.paymentPortalMethodDetails) {
                    this.shoppingCartService
                        .getPaymentMethods(currentCartId)
                        .pipe(takeUntil(this.componentDestroyed$))
                        .subscribe(
                            (methodsResponse: CartPaymentMethodsDetails) => {
                                this.processPaymentMethods(methodsResponse);

                                let resolvedMerchantType = (this.selectedMethod as CartPaymentMethod)?.merchantType;
                                if (
                                    !resolvedMerchantType &&
                                    this.selectedMethod &&
                                    'paymentMethodId' in this.selectedMethod
                                ) {
                                    const card = this.selectedMethod as CartPaymentCard;
                                    const method = methodsResponse.methods?.find(
                                        (m) => m.paymentMethodId === card.paymentMethodId
                                    );
                                    resolvedMerchantType = method?.merchantType;
                                }

                                if (!resolvedMerchantType) {
                                    this.notifications.error(this.translate.instant('PAYMENT_METHODS_LOADING_ERROR'));
                                    this.paymentIsCreating = false;
                                    return;
                                }

                                const initiatePaymentRequest: InitiatePaymentRequest = {
                                    shoppingCartId: currentCartId,
                                    paymentId: this.paymentPortalProviderMethod.payment_id,
                                    queryParams: this.getPaymentParams(saveMethod),
                                    ticketId,
                                    merchantType: resolvedMerchantType
                                };

                                this.paymentPortalService
                                    .initiatePayment(initiatePaymentRequest)
                                    .pipe(takeUntil(this.componentDestroyed$))
                                    .subscribe(
                                        () => {
                                            this.getCart();
                                            this.paymentIsCreating = false;
                                        },
                                        (error) => {
                                            this.paymentIsCreating = false;
                                            this.handleInitiatePaymentError(error);
                                        }
                                    );
                            },
                            () => {
                                this.notifications.errorV2(this.translate.instant('PAYMENT_METHODS_LOADING_ERROR'));
                                this.paymentIsCreating = false;
                            }
                        );
                    return;
                } else {
                    this.notifications.error(this.translate.instant('PAYMENT_METHODS_LOADING_ERROR'));
                    this.paymentIsCreating = false;
                    return;
                }
            }

            const initiatePaymentRequest: InitiatePaymentRequest = {
                shoppingCartId: currentCartId,
                paymentId: this.paymentPortalProviderMethod.payment_id,
                queryParams: this.getPaymentParams(saveMethod),
                ticketId,
                merchantType: merchantType
            };

            const initialPaymentRequestCallback = () => {
                this.getCart();
                this.paymentIsCreating = false;
            };

            const initialPaymentRequestErrorCallback = (error) => {
                this.paymentIsCreating = false;
                this.handleInitiatePaymentError(error);
            };

            this.paymentPortalService
                .initiatePayment(initiatePaymentRequest)
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe(initialPaymentRequestCallback, initialPaymentRequestErrorCallback);
        }
    }

    private handleLockedCartPayment(saveMethod: boolean, ticketId?: number) {
        this.paymentIsCreating = true;

        // Copy the shopping cart
        this.shoppingCartService
            .createCartCopy(this.cart.shoppingCartId)
            .pipe(
                takeUntil(this.componentDestroyed$),
                switchMap((cart) => this.getCartTickets(cart))
            )
            .subscribe(
                ({ cart, tickets }) => {
                    const newTicketId = tickets?.length === 1 ? tickets[0].id : undefined;

                    this.getCart();

                    let merchantType = (this.selectedMethod as CartPaymentMethod)?.merchantType;

                    if (!merchantType && this.selectedMethod && 'paymentMethodId' in this.selectedMethod) {
                        const card = this.selectedMethod as CartPaymentCard;
                        const method = this.paymentPortalMethodDetails?.methods?.find(
                            (m) => m.paymentMethodId === card.paymentMethodId
                        );
                        merchantType = method?.merchantType;
                    }

                    if (this.env.IS_NEW_AFC && !merchantType) {
                        this.notifications.error(this.translate.instant('PAYMENT_METHODS_LOADING_ERROR'));
                        this.paymentIsCreating = false;
                        return;
                    }

                    const initiatePaymentRequest: InitiatePaymentRequest = {
                        shoppingCartId: cart.shoppingCartId,
                        paymentId: this.paymentPortalProviderMethod.payment_id,
                        queryParams: this.getPaymentParams(saveMethod),
                        ticketId: newTicketId || ticketId,
                        merchantType: merchantType
                    };
                    // Initiate the payment with the new shopping cart
                    this.paymentPortalService.initiatePayment(initiatePaymentRequest).subscribe(
                        () => {
                            this.getCart();
                            this.paymentIsCreating = false;
                        },
                        (error) => {
                            this.paymentIsCreating = false;
                            this.handleInitiatePaymentError(error);
                        }
                    );
                },
                (error) => {
                    this.paymentIsCreating = false;
                    this.handleInitiatePaymentError(error);
                }
            );
    }

    private handleInitiatePaymentError(error) {
        if (error.status === 409) {
            this.notifications.error(this.translate.instant('FAILED_TO_REQUEST_NEW_PAYMENT_FROM_PORTAL'));
        }
        if (!navigator.onLine) {
            this.notifications.error(this.translate.instant('NETWORK_ISSUE_PAYMENT_FAILED'));
        }
    }
    private getCorporateMerchantType(): string | undefined {
        const methods: ReadonlyArray<CartPaymentMethod> | undefined = this.paymentPortalMethodDetails?.methods;
        const invoice = methods?.find((m) => m.code === 'invoice');
        return invoice?.merchantType;
    }

    private handleCorporatePayment() {
        if (this.env.IS_NEW_AFC) {
            const currentCartId = this.shoppingCartService.getCartId();
            if (!currentCartId) {
                this.notifications.error(this.translate.instant('LOCAL_NO_CART_FOUND'));
                return;
            }

            const merchantType = this.getCorporateMerchantType();
            if (!merchantType) {
                this.notifications.error(this.translate.instant('PAYMENT_METHODS_LOADING_ERROR'));
                return;
            }

            this.paymentFacade.setCartStatus(null);
            this.paymentIsCreating = true;
            this.accountTicketsFacade.resetAccountTickets();

            if (this.selectedPurpose?.title === TripPurposesTypes.other && !this.otherPurposeValue) {
                return;
            }

            const request: InitiatePaymentRequest = {
                shoppingCartId: currentCartId,
                paymentId: this.paymentPortalProviderMethod?.payment_id,
                merchantType,
                customerAccountId: this.customerAccountId
            };

            this.selectedAccountWithDisplayData$
                .pipe(
                    first((acc) => acc.isCorporate),
                    switchMap((selectedAccount) =>
                        this.addTripPurposeToCart(
                            currentCartId,
                            selectedAccount.account.id,
                            this.selectedPurpose.title === TripPurposesTypes.other
                                ? this.otherPurposeValue
                                : this.selectedPurpose.title
                        )
                    ),
                    switchMap(() => this.initiatePayment(request))
                )
                .subscribe({
                    next: async (response) => {
                        this.shoppingCartService.setCartId(null);
                        this.paymentFacade.setCartStatus(null);

                        if (response.ticketIds?.length === 1) {
                            const ticketId = response.ticketIds[0];
                            await this.router.navigate([this.translate.currentLang, 'account', 'tickets', ticketId]);
                        } else {
                            await this.router.navigate([this.translate.currentLang, 'account', 'tickets']);
                        }

                        this.notifications.success(this.translate.instant('PURCHASE_FLOW_RESULT_SUCCESS'), {
                            showAfterRouteChange: true
                        });
                    },
                    error: (error) => {
                        this.handleInitiatePaymentError(error);
                    }
                });

            return;
        }
        if (this.selectedPurpose?.title === TripPurposesTypes.other && !this.otherPurposeValue) {
            return;
        }

        const payload = {
            trip_purpose:
                this.selectedPurpose?.title === TripPurposesTypes.other
                    ? this.otherPurposeValue
                    : this.selectedPurpose?.title,
            employee_id: this.selectedOrganization.employee_id,
            default_payment: true
        };

        this.shoppingCartService
            .corporatePayment(this.cart.shoppingCartId, payload)
            .pipe(
                tap(async () => {
                    if (this.cart.tickets.length !== 1) {
                        this.shoppingCartService.setCartId(null);
                        await this.router.navigate([this.translate.currentLang, 'account', 'tickets']);
                        return;
                    }
                }),
                switchMap(() => {
                    this.accountTicketsFacade.resetAccountTickets();
                    this.accountTicketsFacade.fetchAccountTickets(0, 0);
                    return this.accountTicketsFacade.accountTickets$;
                }),
                filter((tickets) => !!tickets?.find((ticket) => ticket.docNum === String(this.cart.tickets[0].id))),
                takeUntil(this.componentDestroyed$),
                catchError((error) => {
                    if (error.status === 409 && error.error === 'CORPORATE_ORG_CREDIT_NOT_SUFFICIENT') {
                        this.notifications.error(this.translate.instant('CORPORATE_ORG_CREDIT_NOT_SUFFICIENT'));
                    } else if (error.status === 409 && error.error === 'CORPORATE_EMPLOYEE_NOT_ACTIVE') {
                        this.notifications.error(this.translate.instant('CORPORATE_CREDIT_NOT_ACTIVE'));
                    } else {
                        throw error;
                    }
                    return EMPTY;
                })
            )
            .subscribe(async () => {
                this.shoppingCartService.setCartId(null);
                this.paymentFacade.setCartStatus(null);
                if (this.cart.tickets.length === 1) {
                    await this.router.navigate([
                        this.translate.currentLang,
                        'account',
                        'tickets',
                        this.cart.tickets[0].id
                    ]);
                }
            });
    }

    private handleZeroPricePayment() {
        const currentCartId = this.shoppingCartService.getCartId();

        if (!currentCartId) {
            this.notifications.error(this.translate.instant('LOCAL_NO_CART_FOUND'));
            window.scroll(0, 0);
        }
        this.shoppingCartService
            .zeroPricePayment(currentCartId)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(
                () => {
                    this.shoppingCartService.setCartId(null);
                    this.shoppingCartIconService.initializeHeaderShoppingCart(0, null);
                    this.router.navigate([this.translate.currentLang, 'account', 'tickets']);
                    this.notifications.success(this.translate.instant('ZERO_SUM_PURCHASE_FLOW_RESULT_SUCCESS'), {
                        showAfterRouteChange: true
                    });
                },
                (error) => {
                    console.error(error);
                    this.notifications.error(this.translate.instant('FAILED_TO_REQUEST_NEW_PAYMENT_FROM_PORTAL'));
                }
            );
    }

    isCartLocked() {
        return this.cart?.status === CartStatus.LOCKED;
    }

    showOtherPaymentMethods() {
        this.showOtherPaymentPortalMethod = !this.showOtherPaymentPortalMethod;
    }

    async onRemoveSavedMethodClick() {
        const modal = await this.modalCtrl.create({
            component: DeleteSavedPaymentMethodModalComponent,
            cssClass: 'delete-saved-payment-method-modal',
            componentProps: {
                cards: this.paymentPortalMethodDetails.cards
            }
        });
        await modal.present();

        modal.onDidDismiss().then(({ data }) => {
            if (data) {
                this.notifications.success(this.translate.instant('CART_SAVED_CARD_DELETED_SUCCESSFULLY'), {
                    showAfterRouteChange: true
                });
                this.getPaymentMethods();
            }
        });
    }

    fetchMethods() {
        this.paymentMethodService.fetchStarted();
        this.paymentMethodService.fetchAllMethods().subscribe(
            (paymentMethods) => {
                this.paymentPortalProviderMethod = paymentMethods[0];
            },
            (e) => {
                this.paymentMethodService.fetchFailed(e);
            }
        );
    }

    private getPaymentParams(save) {
        if (!this.selectedMethod) {
            return {};
        }

        const paymentQueryParams: {
            posPaymentMethodId?: string;
            cardId?: string;
            storeNewCard: boolean;
            rememberSelection: boolean;
        } = {
            storeNewCard: save,
            rememberSelection: true
        };

        if (Object.prototype.hasOwnProperty.call(this.selectedMethod, 'posPaymentMethodId')) {
            paymentQueryParams.posPaymentMethodId = (this.selectedMethod as CartPaymentMethod).posPaymentMethodId;
        } else {
            paymentQueryParams.cardId = (this.selectedMethod as CartPaymentCard).id;
        }

        return paymentQueryParams;
    }

    getCartPrice(): number {
        if (this.cart?.price.discountAmount >= this.cart?.price.amount) {
            return 0;
        }

        if (this.cart?.price.discountAmount > 0) {
            return this.cart?.price.amount - this.cart?.price.discountAmount;
        }

        return this.cart?.price.amount;
    }

    get isZeroSumCart(): boolean {
        return this.getCartPrice() === 0;
    }

    getCurrency(): string {
        return this.currency || this.cart.tickets[0]?.price?.currency;
    }

    private getCustomerCreditBalance(customerAccountId: number | string): Observable<number> {
        return this.corporateEmployeesService
            .getCustomerCreditInfo(customerAccountId)
            .pipe(map((creditInfo) => this.calculateAvailableCustomerBalance(creditInfo)));
    }

    private calculateAvailableCustomerBalance(creditInfo: CustomerCreditInfo): number {
        if (!creditInfo) {
            return 0;
        }
        const customerLimit =
            creditInfo.customerAccountMonthlyLimit ?? creditInfo.defaultPerCustomerAccountMonthlyLimit;

        return customerLimit - creditInfo.customerAccountMonthlyUsage;
    }

    get isProceedButtonDisabled(): boolean {
        const termsInvalid = !this.termsAgreed && this.isShowTermsAndConditions;
        const priceIsZero = this.isZeroSumCart;

        const personalPaymentInvalid =
            !priceIsZero &&
            this.selectedPayerType === this.payerType.personal &&
            this.env.PURCHASE_FLOW?.ENABLE_DETAILED_PAYMENT_METHODS &&
            !this.selectedMethod;

        const companyPaymentInvalid =
            !priceIsZero &&
            this.selectedPayerType === this.payerType.company &&
            (!this.enoughCorporateBalance() ||
                (this.env.IS_NEW_AFC && !this.isCorporateCreditActive()) ||
                (!this.env.IS_NEW_AFC && this.isCorporatePurpose()));

        const isOtherPurposeInvalid =
            this.selectedPurpose?.title === TripPurposesTypes.other &&
            (!this.otherPurposeValue || this.otherPurposeValue?.length < this.validOtherPurposeLength);

        const isPurposeInvalid =
            this.selectedPayerType === this.payerType.company &&
            (!this.selectedPurpose?.title || isOtherPurposeInvalid);

        return termsInvalid || personalPaymentInvalid || companyPaymentInvalid || isPurposeInvalid;
    }

    public validatePurposeValue(event: Event) {
        this.purposeValueValidated =
            (event.target as HTMLTextAreaElement).value?.trim().length >= this.validOtherPurposeLength;
    }

    private resolveTermsCheckboxVisibility(): boolean {
        const visibility: TermsCheckboxVisibility =
            this.env.PURCHASE_FLOW?.TICKET_CONF?.SHOPPING_CART?.TERMS_CHECKBOX_VISIBILITY ??
            TermsCheckboxVisibility.ANONYMOUS;

        if (visibility === TermsCheckboxVisibility.EVERYONE) {
            return true;
        }

        if (visibility === TermsCheckboxVisibility.HIDDEN) {
            return false;
        }

        // ANONYMOUS: show only when user isn't logged in
        // For mobile apps: also require device access token
        // For web: just check login status
        const isLoggedOut = !this.authService.getLoginStatus();

        if (this.env.IS_MOBILE_APP) {
            // Mobile app: need device access token in addition to not being logged in
            return isLoggedOut && !!this.deviceIdService.getAccessDeviceToken();
        }

        // Web: just check if user is not logged in
        return isLoggedOut;
    }

    private addTripPurposeToCart(
        cartId: number,
        accountId: number | string,
        purposeTitle: string
    ): Observable<string | Record<string, unknown>> {
        return this.paymentPortalService.addTripPurposeToCart(cartId, accountId, purposeTitle).pipe(
            catchError((error: HttpErrorResponse) => {
                console.error(error);
                // Return a value to allow the stream to continue
                return of('ERROR_ADDING_TRIP_PURPOSE');
            })
        );
    }

    private initiatePayment(request: InitiatePaymentRequest): Observable<InitiatePaymentResponse> {
        return this.paymentPortalService.initiatePayment(request).pipe(
            takeUntil(this.componentDestroyed$),
            finalize(() => (this.paymentIsCreating = false))
        );
    }
}
